import { experienceEvent } from '@/events/builders'
import type { EventTuple } from '@/events/types'
import type { PageEventHandler } from '@/play/pageTypes'
import { parseKeyX, parseKeyY, parseKeyZ, type Position } from '@/voxelMath'
import type { VoxelData, VoxelMap } from '@/voxelTypes'
import { TINT_LIQUID, type LiquidKind } from './kind'

/**
 * Applying and merging the sim's changes (`LiquidSim.drainChanges`): `clear` removes liquid at keys
 * that hold liquid; `set` writes liquid of `color` into keys that are empty or hold liquid. Terrain
 * (anything that is not liquid of the kind) is never overwritten or removed. The kind defaults to
 * ink (Spill).
 */

/** Same-shaped as `LiquidSim`'s change sets (kept here so reducers need not import the sim). */
export type LiquidChangeSet = { set: Array<[key: number, color: number]>; clear: number[] }

/** Shared, frozen ink voxel per colour (cheap palette interning by identity). */
export function liquidVoxel(color: number): VoxelData {
  return TINT_LIQUID.voxel(color)
}

function inBounds(key: number, bounds: Position | undefined): boolean {
  if (!bounds) return true
  return parseKeyX(key) < bounds.x && parseKeyY(key) < bounds.y && parseKeyZ(key) < bounds.z
}

/** Applies changes to a mutable base. Returns the number of cells changed. */
export function applyLiquidChanges(base: VoxelMap, changes: LiquidChangeSet, bounds?: Position, kind: LiquidKind = TINT_LIQUID): number {
  let changed = 0
  const clear = changes?.clear ?? []
  const set = changes?.set ?? []
  for (let k = 0; k < clear.length; k++) {
    const key = clear[k]
    if (kind.matches(base.get(key))) {
      base.delete(key)
      changed++
    }
  }
  for (let k = 0; k < set.length; k++) {
    const [key, color] = set[k]
    if (!Number.isInteger(key) || key < 0 || !inBounds(key, bounds)) continue
    const existing = base.get(key)
    if (existing && !kind.matches(existing)) continue
    const v = kind.voxel(color)
    if (existing && (existing.color ?? kind.defaultColor) === v.color) continue
    base.set(key, v)
    changed++
  }
  return changed
}

/**
 * Merges two change sets (b after a) into one, e.g. to draft per-frame deltas into one undo step.
 * Per key the later entry wins; a clear after a set of the same key is a clear.
 */
export function mergeLiquidChanges(a: LiquidChangeSet, b: LiquidChangeSet): LiquidChangeSet {
  const merged = new Map<number, number>()
  const fold = (p: LiquidChangeSet) => {
    for (const key of p?.clear ?? []) merged.set(key, -1)
    for (const [key, color] of p?.set ?? []) merged.set(key, color >>> 0)
  }
  fold(a)
  fold(b)
  const set: Array<[number, number]> = []
  const clear: number[] = []
  for (const [key, color] of merged) {
    if (color === -1) clear.push(key)
    else set.push([key, color])
  }
  return { set, clear }
}

/**
 * A change set being folded in place, with where each key sits in its `set` or `clear` array, so
 * folding one more change set in costs O(that set), not O(everything so far).
 */
type LiquidChangeAccumulator = { acc: LiquidChangeSet; setAt: Map<number, number>; clearAt: Map<number, number> }

/** Removes index `i` of `arr` by moving the last entry there; keeps `at` in step. */
function swapRemove<T>(arr: T[], at: Map<number, number>, i: number, keyOf: (v: T) => number): void {
  const last = arr.length - 1
  if (i !== last) {
    const moved = arr[last]
    arr[i] = moved
    at.set(keyOf(moved), i)
  }
  arr.pop()
}

/** Folds `b` into the accumulator in place, as `mergeLiquidChanges(acc, b)` would. */
function foldInto(a: LiquidChangeAccumulator, b: LiquidChangeSet): void {
  const { acc, setAt, clearAt } = a
  const setKey = (e: [number, number]) => e[0]
  const clearKey = (k: number) => k
  for (const key of b?.clear ?? []) {
    const si = setAt.get(key)
    if (si !== undefined) {
      setAt.delete(key)
      swapRemove(acc.set, setAt, si, setKey)
    }
    if (!clearAt.has(key)) {
      clearAt.set(key, acc.clear.length)
      acc.clear.push(key)
    }
  }
  for (const [key, color] of b?.set ?? []) {
    const ci = clearAt.get(key)
    if (ci !== undefined) {
      clearAt.delete(key)
      swapRemove(acc.clear, clearAt, ci, clearKey)
    }
    const si = setAt.get(key)
    if (si !== undefined) acc.set[si] = [key, color >>> 0]
    else {
      setAt.set(key, acc.set.length)
      acc.set.push([key, color >>> 0])
    }
  }
}

/**
 * A page's durable "liquid settled" event (`experience.<page>.<verb>`, payload a change set) for a
 * liquid kind: the reducer handlers to list in src/pages/events.ts and the pairwise coalescer a
 * draft session (src/play/draft.ts) uses to fold per-frame deltas into one undo step.
 *
 * `accumulate` (default true): the coalescer extends one change set in place instead of building a
 * new merged set per pair. The draft commit folds a whole press left to right (`acc = combine(acc,
 * next)`), so the pairwise merge costs O(events x cells): about 0.7 s for 1800 events of 10 cells
 * and 3.2 s for 3600, a stall at the end of a long stroke. Accumulating is O(cells). The first
 * combine of a fold copies `a`; only the set this coalescer made for the fold in progress is ever
 * extended (it holds the index of one fold at a time), so no logged event is mutated. Same result as the pairwise merge (order inside `set`/`clear` may differ; the reducer
 * does not depend on it).
 */
export function liquidSettleEvent(name: string, kind: LiquidKind, opts: { accumulate?: boolean } = {}) {
  const accumulate = opts.accumulate ?? true
  // The set this coalescer is extending (only the latest fold's; an older one is let go).
  let current: LiquidChangeAccumulator | null = null
  // Settle events are page events (`experience.<page>.<verb>`; the reducer finds them by name).
  const tag = name as `experience.${string}`
  return {
    name,
    event: (changes: LiquidChangeSet): EventTuple => experienceEvent(tag, changes),
    combine: (a: EventTuple, b: EventTuple): EventTuple | null => {
      if (a[0] !== name || b[0] !== name) return null
      const pa = a[1] as LiquidChangeSet
      const pb = b[1] as LiquidChangeSet
      if (!accumulate) return experienceEvent(tag, mergeLiquidChanges(pa, pb))
      if (current && current.acc === pa) {
        foldInto(current, pb)
        return a
      }
      current = { acc: { set: [], clear: [] }, setAt: new Map(), clearAt: new Map() }
      foldInto(current, pa)
      foldInto(current, pb)
      return experienceEvent(tag, current.acc)
    },
    handlers: {
      [name]: (ctx, event) => {
        applyLiquidChanges(ctx.base, event[1] as LiquidChangeSet, ctx.meta?.bounds, kind)
      },
    } as Record<string, PageEventHandler>,
  }
}
